import { useEffect, useState } from 'react';

/**
 * On-screen viewport readout.
 *
 * The bottom tab bar sitting above the home indicator took several attempts
 * to fix, because the failure only happens in an INSTALLED iOS PWA and every
 * measurement available to me comes from a desktop browser emulating an
 * iPhone — which resolves viewport units the way a desktop browser does.
 *
 * This makes the device report its own numbers so a single screenshot
 * settles it. Two things about it were wrong and are fixed here:
 *
 * 1. It was reachable only at `?debug=viewport`. An installed PWA has no
 *    URL bar, so in the one mode where the bug happens it could not be
 *    opened at all. It now also responds to a flag set from Settings.
 *
 * 2. It computed the gap below the tab bar as `innerHeight - nav.bottom`.
 *    That is circular: `innerHeight` under-reports in exactly the failing
 *    configuration, so a real gap read as zero. The honest comparison is
 *    against the physical screen, so both are shown and disagreement
 *    between them is itself the diagnosis.
 *
 * 3. It had no way to close itself. The overlay is `position: fixed; top: 0`
 *    with `z-index: 9999`, which sits directly on top of the Settings
 *    page's "Layout diagnostic" card -- including the very toggle button
 *    that turns it off. `pointerEvents: 'none'` on the box means a tap
 *    there passes through to the real button underneath, but there is no
 *    way to SEE that button to know where to tap, so "turn it off" reads as
 *    broken even though the toggle itself still works. Fixed by putting an
 *    explicit Close button on the overlay itself (the one element inside it
 *    with `pointerEvents: 'auto'`), so it doesn't depend on a hidden
 *    control underneath. Closing clears both ways it can be turned on (the
 *    Settings flag AND a `?debug=viewport` URL param, if present) --
 *    clearing only one left it reopening itself if the other was still set.
 */
export function ViewportDebug() {
  const [, tick] = useState(0);

  useEffect(() => {
    const onAny = () => tick((n) => n + 1);
    window.addEventListener('resize', onAny);
    window.addEventListener('orientationchange', onAny);
    window.visualViewport?.addEventListener('resize', onAny);
    window.visualViewport?.addEventListener('scroll', onAny);
    const id = window.setInterval(onAny, 500);
    return () => {
      window.removeEventListener('resize', onAny);
      window.removeEventListener('orientationchange', onAny);
      window.visualViewport?.removeEventListener('resize', onAny);
      window.visualViewport?.removeEventListener('scroll', onAny);
      window.clearInterval(id);
    };
  }, []);

  if (typeof window === 'undefined') return null;
  const byQuery = new URLSearchParams(window.location.search).get('debug') === 'viewport';
  let byFlag = false;
  try {
    byFlag = window.localStorage.getItem('bn:debugViewport') === '1';
  } catch {
    byFlag = false;
  }
  if (!byQuery && !byFlag) return null;

  const close = () => {
    try {
      window.localStorage.setItem('bn:debugViewport', '0');
    } catch {
      /* private mode -- the query param branch below still clears */
    }
    if (byQuery) {
      const url = new URL(window.location.href);
      url.searchParams.delete('debug');
      window.history.replaceState(null, '', url.pathname + url.search + url.hash);
    }
    // The native `storage` event only fires in OTHER tabs, not this one, so
    // Settings' own toggle (which reads the flag once at mount) would show
    // stale -- "Hide" after the overlay is already gone -- without this.
    window.dispatchEvent(new Event('bn:debugViewport-changed'));
    tick((n) => n + 1);
  };

  const shell = document.querySelector('.app-shell')?.getBoundingClientRect();
  const nav = document.querySelector('.bottom-nav')?.getBoundingClientRect();
  const probe = document.createElement('div');
  probe.style.cssText =
    'position:fixed;bottom:env(safe-area-inset-bottom,0px);left:0;width:0;height:0;';
  document.body.appendChild(probe);
  const safeBottom = Math.round(parseFloat(getComputedStyle(probe).bottom) || 0);
  document.body.removeChild(probe);

  const vvH = window.visualViewport?.height ?? 0;
  const vvTop = window.visualViewport?.offsetTop ?? 0;
  const vvBottom = vvTop + vvH;
  const rows: [string, string][] = [
    ['innerHeight', String(window.innerHeight)],
    ['visualViewport.h', String(Math.round(vvH))],
    ['visualViewport.offsetTop', String(Math.round(vvTop))],
    ['screen.height', String(window.screen?.height ?? 0)],
    ['documentElement.clientH', String(document.documentElement.clientHeight)],
    ['--app-height', getComputedStyle(document.documentElement).getPropertyValue('--app-height').trim() || '(unset)'],
    ['--app-top', getComputedStyle(document.documentElement).getPropertyValue('--app-top').trim() || '(unset)'],
    ['safe-area-bottom', `${safeBottom}px`],
    ['shell top/bottom', shell ? `${Math.round(shell.top)} / ${Math.round(shell.bottom)}` : 'n/a'],
    ['shell height', shell ? String(Math.round(shell.height)) : 'n/a'],
    ['nav bottom', nav ? String(Math.round(nav.bottom)) : 'n/a'],
    [
      'GAP vs visualViewport',
      nav ? String(Math.round(vvBottom - nav.bottom)) : 'n/a',
    ],
    [
      'GAP vs innerHeight',
      nav ? String(Math.round(window.innerHeight - nav.bottom)) : 'n/a',
    ],
    [
      'GAP vs screen',
      nav ? String(Math.round((window.screen?.height ?? 0) - nav.bottom)) : 'n/a',
    ],
    ['standalone', String(!!(window.navigator as unknown as { standalone?: boolean }).standalone)],
    ['dpr', String(window.devicePixelRatio)],
  ];

  return (
    <div
      style={{
        position: 'fixed',
        top: 'env(safe-area-inset-top, 0px)',
        left: 8,
        right: 8,
        zIndex: 9999,
        background: 'rgba(20,24,26,0.94)',
        color: '#F5F1E8',
        font: '600 11px/1.45 ui-monospace, Menlo, Consolas, monospace',
        padding: '10px 12px',
        borderRadius: 4,
        pointerEvents: 'none',
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
        <span style={{ opacity: 0.65, letterSpacing: '0.06em' }}>VIEWPORT DEBUG</span>
        <button
          type="button"
          onClick={close}
          style={{
            pointerEvents: 'auto',
            background: 'rgba(245,241,232,0.14)',
            color: '#F5F1E8',
            border: 'none',
            borderRadius: 3,
            font: '700 11px/1 ui-monospace, Menlo, Consolas, monospace',
            padding: '5px 10px',
            cursor: 'pointer',
          }}
        >
          Close
        </button>
      </div>
      {rows.map(([k, v]) => (
        <div key={k} style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
          <span style={{ opacity: 0.7 }}>{k}</span>
          <span>{v}</span>
        </div>
      ))}
      <div style={{ opacity: 0.6, marginTop: 6 }}>
        Chrome webpage: GAP vs innerHeight should read 0 (VV-only undershoots).
        A2HS: GAP vs visualViewport should read 0.
      </div>
    </div>
  );
}
